import {ClassComponent, Vnode, VnodeDOM} from 'mithril';
import Picture, {ImageInfo} from '../models/Picture';

interface PictureViewBoxAttrs {
    picture: Picture
}

const PADDING = 40;

export default class PictureViewBox implements ClassComponent<PictureViewBoxAttrs> {
    image!: ImageInfo
    loopResizer: boolean = true
    wasResized: boolean = true
    resizeListener!: () => void

    oninit(vnode: Vnode<PictureViewBoxAttrs>) {
        // TODO: in the future we might want to select a new image inside of the resize listener based on the new size
        // The challenge is selecting the picture during oninit since we don't have access to the container bounding box yet
        this.image = vnode.attrs.picture.bestImage(2048);
    }

    view(vnode: Vnode<PictureViewBoxAttrs>) {
        const {picture} = vnode.attrs;

        const [src] = this.image;

        const stickers = picture.stickers() || [];

        return m('.PictureViewBoxContainer', m('.PictureViewBox.js-picture-resize', [
            m('img.PictureViewBoxImage', {
                src,
                alt: picture.title() || 'User upload without title',
            }),
            stickers.length ? m('.PictureViewBoxStickers', stickers.map(sticker => {
                const style: any = {
                    '--proportional-top': sticker.top,
                    '--proportional-left': sticker.left,
                };

                if (sticker.width) {
                    style['--proportional-width'] = sticker.width;
                }

                if (sticker.height) {
                    style['--proportional-height'] = sticker.height;
                }

                if (sticker.angle) {
                    style['--angle'] = sticker.angle;
                }

                if (sticker.radius) {
                    style['--proportional-width'] = sticker.radius * 2;
                    style['--proportional-height'] = sticker.radius * 2;
                }

                if (sticker.color) {
                    style['--color'] = sticker.color;
                }

                if (sticker.url) {
                    style.backgroundImage = 'url(' + sticker.url + ')';
                }

                return m('.PictureViewBoxSticker', {
                    'data-type': sticker.type,
                    style,
                    title: sticker.label,
                });
            })) : null,
        ]));
    }

    resize(container: HTMLElement): void {
        if (!this.loopResizer) {
            return;
        }

        if (this.wasResized) {
            this.wasResized = false;

            const {width, height} = container.getBoundingClientRect();

            const containerRatio = width / height;
            const imageRatio = this.image[1] / this.image[2];

            const box = container.querySelector('.js-picture-resize') as HTMLElement;

            if (containerRatio > imageRatio) {
                const targetHeight = Math.min(height - PADDING, this.image[2]);

                box.style.setProperty('--height', targetHeight + 'px');
                box.style.setProperty('--width', (targetHeight * imageRatio) + 'px');
            } else {
                const targetWidth = Math.min(width - PADDING, this.image[1]);

                box.style.setProperty('--width', targetWidth + 'px');
                box.style.setProperty('--height', (targetWidth / imageRatio) + 'px');
            }
        }

        window.requestAnimationFrame(() => {
            this.resize(container);
        });
    }

    oncreate(vnode: VnodeDOM<PictureViewBoxAttrs>) {
        this.resize(vnode.dom as HTMLElement);

        this.resizeListener = () => {
            this.wasResized = true;
        };

        window.addEventListener('resize', this.resizeListener);
    }

    onremove() {
        this.loopResizer = false;

        window.removeEventListener('resize', this.resizeListener);
    }
}
